import { GitMerge, GitPullRequest, GitPullRequestClosed, GitPullRequestDraft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PrAssociationState } from '@reflex/shared';

interface PrStateMeta {
  icon: LucideIcon;
  label: string;
  badgeColor: string;
  iconColor: string;
}

/**
 * Per-state presentation for a pull-request association: the icon, the short
 * status label shown in the popover / overflow list, and the color utilities
 * layered over `PULL_REQUEST_CHIP_BADGE_CLASSNAME`. `open` reuses the base (info)
 * colors, so its `badgeColor` override is empty. Draft reads muted with the
 * dashed draft icon, merged reads purple (GitHub's merged color), and closed
 * reads red, so the PR's state is legible at a glance. Only color changes — the
 * hidden measurement row stays width-accurate.
 *
 * Lives in its own module (not `PullRequestChip.tsx`) so the chip and its
 * consumers (the agent association strip's overflow list) can share it
 * without tripping `react-refresh/only-export-components` on a non-component
 * export.
 */
export const PR_STATE_META: Record<PrAssociationState, PrStateMeta> = {
  open: {
    icon: GitPullRequest,
    label: 'Open',
    badgeColor: '',
    iconColor: 'text-status-info',
  },
  draft: {
    icon: GitPullRequestDraft,
    label: 'Draft',
    badgeColor:
      'border-status-neutral/25 bg-status-neutral/10 text-status-neutral hover:bg-status-neutral/20',
    iconColor: 'text-status-neutral',
  },
  merged: {
    icon: GitMerge,
    label: 'Merged',
    badgeColor:
      'border-status-accent-purple/25 bg-status-accent-purple/10 text-status-accent-purple hover:bg-status-accent-purple/20',
    iconColor: 'text-status-accent-purple',
  },
  closed: {
    icon: GitPullRequestClosed,
    label: 'Closed',
    badgeColor:
      'border-status-error/25 bg-status-error/10 text-status-error hover:bg-status-error/20',
    iconColor: 'text-status-error',
  },
};

/**
 * The state the chip actually renders: the stored association states plus
 * `unknown` — the greyed, still-loading look for a surface with *nothing*
 * stored to fall back on (a bare PR link in prose, a composer mention), where
 * the only alternative would be to invent a state. A chip that was handed a
 * stored state renders that instead; see `AgentPRChip`.
 */
export type PrDisplayState = PrAssociationState | 'unknown';

/**
 * Presentation for every {@link PrDisplayState}: {@link PR_STATE_META} plus the
 * one display-only state, which stays neutral and dimmed so the chip is
 * visibly "not yet confirmed" rather than claiming a status.
 */
export const PR_DISPLAY_META: Record<PrDisplayState, PrStateMeta> = {
  ...PR_STATE_META,
  unknown: {
    icon: GitPullRequest,
    label: 'Checking status…',
    badgeColor:
      'border-status-neutral/25 bg-status-neutral/10 text-status-neutral opacity-70 hover:bg-status-neutral/20',
    iconColor: 'text-status-neutral',
  },
};
